import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
    EntityPanelFormGroup,
    EntityPanelGroupHeader,
} from '../../../../panels/entity-panels/entity-panel-components';
import { NoMembersIndicator } from '../../../tabs/components/no-members-indicator';
import { TabCard } from '../../../tabs/components/tab-card';
import { ConditionalWrapper } from '../../../../common/components/conditional-wrapper';
import { EventPanelConfiguration, EventPanelFormType, EventPanelOptions } from '@mark43/cad-api';
import { useTrackedFieldArray } from '../../../../forms/use-tracked-field-array';
import { EventPanelFormPersonFieldset } from './event-panel-form-person-fieldset';
import { useIsFieldEditable } from '../../use-is-field-editable';
import { FormInputButton } from '../../../../forms/integrated-form-components/form-button';
import { Box } from '@cad-arc';
import { getDefaultPerson } from './helpers';
import { EntityPanelFormPersonBaseKey } from '../../../../panels/entity-panels/forms/types';

export type EventPanelFormPersonsFieldsProps = {
    form: UseFormReturn<EventPanelFormType>;
    allowMembersControl?: boolean;
    baseKey: EntityPanelFormPersonBaseKey;
    noMembersText?: string;
    addButtonText?: string;
    showTitle?: boolean;
    displayAsCards?: boolean;
    eventPanelOptions: EventPanelOptions;
    eventPanelConfig?: EventPanelConfiguration;
    isReadOnly?: boolean;
    userManuallyAddedPersonField?: boolean;
};

export function EventPanelFormPersonsFields(
    props: EventPanelFormPersonsFieldsProps
): React.ReactElement {
    const [userManuallyAddedField, setUserManuallyAddedField] = useState(false);

    const eventForm = props.form;
    const { fields, append, remove } = useTrackedFieldArray({
        control: eventForm.control,
        name: props.baseKey,
        keyName: 'rhfKey',
    });
    const isInvolvedPersons =
        props.baseKey === 'involvedPersons' || props.baseKey === 'mergedInvolvedPersons';
    const isFieldEditable = useIsFieldEditable();

    const addPersonButton =
        props.allowMembersControl && isFieldEditable ? (
            <FormInputButton
                size="md"
                variant="ghost"
                leadingVisual="Add"
                data-testid={`add-${isInvolvedPersons ? 'involved-' : ''}person-button`}
                onClick={() => {
                    append(getDefaultPerson(props.eventPanelConfig));
                    setUserManuallyAddedField(true);
                }}
            >
                {props.addButtonText}
            </FormInputButton>
        ) : undefined;

    return (
        <EntityPanelFormGroup
            data-testid={
                props.baseKey === 'reportingParties'
                    ? 'reporting-party-form-section'
                    : 'involved-persons-form-section'
            }
        >
            {fields.length !== 0 && props.showTitle ? (
                <EntityPanelGroupHeader
                    title={
                        props.baseKey === 'reportingParties'
                            ? 'Reporting Party'
                            : 'Involved Persons'
                    }
                />
            ) : undefined}
            {props.noMembersText && fields.length === 0 ? (
                <NoMembersIndicator
                    text={props.noMembersText}
                    icon="Person"
                    action={addPersonButton}
                    value={fields}
                />
            ) : (
                <>
                    {fields.map((field, index) => {
                        return (
                            <ConditionalWrapper
                                key={field.rhfKey}
                                condition={props.displayAsCards}
                                wrapper={(children) => <TabCard>{children}</TabCard>}
                            >
                                <EventPanelFormPersonFieldset
                                    eventPanelOptions={props.eventPanelOptions}
                                    key={field.rhfKey}
                                    field={field}
                                    index={index}
                                    remove={remove}
                                    baseKey={props.baseKey}
                                    allowMembersControl={props.allowMembersControl}
                                    displayAsCards={props.displayAsCards}
                                    isReadOnly={props.isReadOnly}
                                    userManuallyAddedField={
                                        props.userManuallyAddedPersonField || userManuallyAddedField
                                    }
                                />
                            </ConditionalWrapper>
                        );
                    })}
                    <Box>{addPersonButton}</Box>
                </>
            )}
        </EntityPanelFormGroup>
    );
}
